/**
 * Login Lifecycle Logger
 * =====================
 * 
 * Provides structured logging for the login flow with correlation IDs
 * to diagnose race conditions between auth success and bootstrap completion.
 * 
 * Work Order: PROD Login "First Attempt Fails, Second Attempt Works"
 */

import { nanoid } from 'nanoid';

export type LoginEvent =
  | 'LOGIN_CLICKED'
  | 'FIREBASE_SIGNIN_START'
  | 'FIREBASE_SIGNIN_OK'
  | 'FIREBASE_SIGNIN_ERR'
  | 'BOOTSTRAP_START'
  | 'BOOTSTRAP_SKIP_CLAIMS'
  | 'BOOTSTRAP_ENSURE_USER_DOC_START'
  | 'BOOTSTRAP_ENSURE_USER_DOC_OK'
  | 'BOOTSTRAP_ENSURE_USER_DOC_ERR'
  | 'BOOTSTRAP_SET_CLAIMS_START'
  | 'BOOTSTRAP_SET_CLAIMS_OK'
  | 'BOOTSTRAP_SET_CLAIMS_ERR'
  | 'IDTOKEN_REFRESH_START'
  | 'IDTOKEN_REFRESH_OK'
  | 'IDTOKEN_REFRESH_ERR'
  | 'CLAIMS_POLL_START'
  | 'CLAIMS_POLL_OK'
  | 'CLAIMS_POLL_TIMEOUT'
  | 'AUTH_GUARD_DECISION'
  | 'LOGIN_SUCCESS_NAVIGATE'
  | 'LOGIN_UI_ERROR_SHOWN';

export type ErrorBucket = 'AUTH' | 'BOOTSTRAP' | 'GUARD' | 'UNKNOWN';

interface LoginLogEntry {
  attemptId: string;
  event: LoginEvent;
  timestamp: string;
  duration?: number;
  data?: Record<string, unknown>;
  errorBucket?: ErrorBucket;
}

class LoginLogger {
  private currentAttemptId: string | null = null;
  private attemptStartTime: number = 0;
  private logs: LoginLogEntry[] = [];

  /**
   * Start a new login attempt
   */
  startAttempt(email: string): string {
    this.currentAttemptId = nanoid(10);
    this.attemptStartTime = Date.now();
    this.logs = [];

    this.log('LOGIN_CLICKED', { email: this.maskEmail(email) });
    return this.currentAttemptId;
  }

  /**
   * Log an event in the current login attempt
   */
  log(
    event: LoginEvent,
    data?: Record<string, unknown>,
    errorBucket?: ErrorBucket
  ): void {
    if (!this.currentAttemptId) {
      console.warn('[LoginLogger] No active attempt, creating one');
      this.currentAttemptId = nanoid(10);
      this.attemptStartTime = Date.now();
    }

    const entry: LoginLogEntry = {
      attemptId: this.currentAttemptId,
      event,
      timestamp: new Date().toISOString(),
      duration: Date.now() - this.attemptStartTime,
      data,
      errorBucket,
    };

    this.logs.push(entry);

    // Console output for debugging
    const emoji = this.getEventEmoji(event);
    console.log(
      `[LoginLogger ${emoji}] ${event}`,
      {
        attemptId: this.currentAttemptId,
        duration: `${entry.duration}ms`,
        ...data,
      }
    );
  }

  /**
   * Get all logs for the current attempt
   */
  getLogs(): LoginLogEntry[] {
    return [...this.logs];
  }

  /**
   * Clear the current attempt
   */
  clearAttempt(): void {
    this.currentAttemptId = null;
    this.attemptStartTime = 0;
    this.logs = [];
  }

  /**
   * Get current attempt ID
   */
  getCurrentAttemptId(): string | null {
    return this.currentAttemptId;
  }

  private maskEmail(email: string): string {
    const [local, domain] = email.split('@');
    if (!domain) return '***';
    return `${local.slice(0, 2)}***@${domain}`;
  }

  private getEventEmoji(event: LoginEvent): string {
    if (event.includes('_OK')) return '✅';
    if (event.includes('_ERR')) return '❌';
    if (event.includes('_START')) return '🔄';
    if (event.includes('TIMEOUT')) return '⏰';
    if (event === 'LOGIN_CLICKED') return '🔐';
    if (event === 'LOGIN_SUCCESS_NAVIGATE') return '🚀';
    return '📊';
  }
}

// Singleton instance
export const loginLogger = new LoginLogger();
